import Title from '@cohort/components-xps/components/Title';
import {isEmailValid} from '@cohort/shared/utils/validation';
import {useTurnstile} from '@cohort/shared-frontend/hooks/useTurnstile';
import {classNames} from '@cohort/shared-frontend/utils/classNames';
import CohortButton from '@cohort/wallet/components/button/CohortButton';
import SecondaryButton from '@cohort/wallet/components/button/SecondaryButton';
import {Input} from '@cohort/wallet/components/forms/Input';
import {DividerWithText} from '@cohort/wallet/components/modals/auth/DividerWithText';
import EmailVerificationModal from '@cohort/wallet/components/modals/auth/EmailVerificationModal';
import {Subtitle} from '@cohort/wallet/components/Subtitle';
import SvgIcon from '@cohort/wallet/components/Svg';
import {Transition} from '@cohort/wallet/components/transitions/Transition';
import {useAppStore} from '@cohort/wallet/hooks/stores/app';
import {useUserStore} from '@cohort/wallet/hooks/stores/user';
import {useApi} from '@cohort/wallet/hooks/useApi';
import {useMerchantContext} from '@cohort/wallet/hooks/useMerchantContext';
import useShopify from '@cohort/wallet/hooks/useShopify';
import useThemeContext from '@cohort/wallet/hooks/useThemeContext';
import {loginWithGoogle} from '@cohort/wallet/lib/Authentication';
import {UserApi} from '@cohort/wallet/lib/Endpoints';
import {Colors} from '@cohort/wallet/lib/Theme';
import {trackError} from '@cohort/wallet/lib/Tracking';
import {isKnownBrowser} from '@cohort/wallet/lib/UserAgent';
import type {CampaignStoreWDto} from '@cohort/wallet-schemas/campaign';
import {CheckCircle} from '@phosphor-icons/react';
import {FirebaseError} from 'firebase/app';
import React, {Fragment, useCallback, useEffect, useState} from 'react';
import {Trans, useTranslation} from 'react-i18next';
import {Navigate, useMatch} from 'react-router-dom';

type LogInProps = {
  campaign?: CampaignStoreWDto;
  isForBuying: boolean;
  errorCallback?: () => void;
};

export const LogIn: React.FC<LogInProps> = ({isForBuying, errorCallback, campaign}) => {
  const merchant = useMerchantContext();
  const emailHint = useUserStore(store => store.initEmail);
  const embedded = useAppStore(store => store.embedded);
  const [email, setEmail] = useState(emailHint ?? '');
  const [showEmailVerification, setShowEmailVerification] = useState(false);
  const [initialized, setInitialized] = useState(false);
  const {token, resetToken} = useTurnstile();
  const isSpace = useMatch('/space/*');

  const {t} = useTranslation('components', {keyPrefix: 'modals.auth.login'});
  const sendSigninCode = useApi(UserApi.sendSigninCode);
  const shopify = useShopify();
  const {accentColor, hasDarkAccent, hasDarkBg} = useThemeContext();

  useEffect(() => {
    if (shopify.active) {
      shopify.signInRedirect();
    } else {
      setInitialized(true);
    }
  }, [shopify]);

  const handleGoogleSignIn = useCallback(async () => {
    if (!merchant.googleSigninEnabled) {
      return;
    }
    try {
      await loginWithGoogle(merchant, emailHint);
    } catch (err: unknown) {
      if (
        err instanceof FirebaseError &&
        ['auth/popup-closed-by-user', 'auth/cancelled-popup-request'].includes(err.code)
      ) {
        return;
      }
      trackError(err);
      errorCallback?.();
    }
  }, [emailHint, errorCallback, merchant]);

  const handleEmailSignIn = useCallback(
    async (e: React.FormEvent<HTMLFormElement>) => {
      e.preventDefault();
      if (!token) {
        return;
      }
      try {
        await sendSigninCode(email, merchant.slug, token);
        setShowEmailVerification(true);
      } catch (err: unknown) {
        trackError(err);
      } finally {
        resetToken?.();
      }
    },
    [token, sendSigninCode, email, merchant.slug, resetToken]
  );

  const onHandleGoBack = useCallback(() => {
    setShowEmailVerification(false);
    setEmail('');
  }, [setShowEmailVerification]);

  if (!initialized) {
    return <Fragment />;
  }

  if (isSpace && embedded) {
    return <Navigate to="/space/session-lost" replace />;
  }

  if (showEmailVerification) {
    return (
      <Transition enterFrom="top" leaveTo="bottom" show twClasses="overflow-visible">
        <EmailVerificationModal
          email={email}
          merchantSlug={merchant.slug}
          errorCallback={errorCallback}
          handleGoBack={onHandleGoBack}
          hideEmailChange={emailHint !== undefined}
        />
      </Transition>
    );
  }

  const computedAccentColor = accentColor;
  const computedBtnColor = hasDarkAccent ? Colors.White : Colors.Black;
  const termsClassName = classNames(
    'text-xs leading-5',
    hasDarkBg ? 'text-white/50' : 'text-black/50'
  );

  return (
    <Transition appear enterFrom="left" leaveTo="right" show twClasses="overflow-visible">
      <div className="space-y-5">
        <div className="flex h-full flex-col items-center text-center md:space-y-1 md:text-left">
          <Title content={t('title')} />
          <Subtitle
            classes="text-center md:text-left"
            content={
              isForBuying
                ? campaign !== undefined && campaign.store.pricing === 'paid'
                  ? t('checkoutSubtitle')
                  : t('checkoutSubtitleFree')
                : t('signinSubtitle')
            }
          />
        </div>
        <form onSubmit={handleEmailSignIn}>
          <label
            htmlFor="email"
            className={classNames(
              'block text-sm font-medium',
              hasDarkBg ? 'text-white/70' : 'text-black/70'
            )}
          >
            {t('emailLabel')}
          </label>
          <div className="mt-1 h-full flex-col space-x-0 space-y-3 md:flex md:space-x-0">
            <div className="relative flex">
              <Input
                testId="email"
                id="email"
                type="text"
                name="email"
                autoComplete="email"
                accentColor={computedAccentColor}
                placeholder="vitalik@getcohort.com"
                hasDarkBg={hasDarkBg}
                autoFocus
                value={email}
                onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                  setEmail(event.target.value)
                }
                disabled={emailHint !== undefined}
                width="w-full"
                required
              />

              <Transition
                show={isEmailValid(email)}
                twClasses="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-3"
                enterFrom="bottom"
                leaveTo="top"
              >
                <CheckCircle className="h-5 w-5 text-green-500" aria-hidden="true" />
              </Transition>
            </div>

            <CohortButton
              testId="continue"
              type="submit"
              disabled={!isEmailValid(email)}
              className="w-full"
              style={{
                backgroundColor: computedAccentColor,
                color: computedBtnColor,
              }}
              text={t('continue')}
              tracking={{namespace: 'login.email'}}
            />
          </div>
        </form>

        {isKnownBrowser() && merchant.googleSigninEnabled && (
          <div className="space-y-3">
            <DividerWithText text={t('divider')} />
            <SecondaryButton
              text={
                <Fragment>
                  <SvgIcon name="Google" height={24} className="mr-[17px] align-middle" />
                  {t('google')}
                </Fragment>
              }
              onClick={async () => handleGoogleSignIn()}
              className={classNames(
                'inline-flex h-12 w-full items-center justify-center rounded-md border border-transparent px-3 py-2  text-sm font-medium leading-4 transition duration-300 ease-in-out',
                hasDarkBg
                  ? 'bg-white/5 text-white/70 hover:bg-white/10 focus:ring-white/50'
                  : 'bg-black/5 text-black/70 hover:bg-black/10 focus:ring-black/50'
              )}
              tracking={{namespace: 'login.google'}}
            />
          </div>
        )}
        <div>
          <p className={termsClassName}>
            <Trans
              i18nKey="modals.auth.login.recaptchaTos"
              ns="components"
              components={{
                linkPrivacy: (
                  <a
                    title="privacy"
                    className="underline"
                    href="https://www.cloudflare.com/privacypolicy/"
                    target="_blank"
                    rel="noreferrer noopener"
                  />
                ),
                linkTos: (
                  <a
                    title="Tos"
                    className="underline"
                    href="https://www.cloudflare.com/website-terms/"
                    target="_blank"
                    rel="noreferrer noopener"
                  />
                ),
              }}
            />
          </p>
          <p className={termsClassName}>
            <Trans
              i18nKey="modals.auth.login.rgpdTerms"
              ns="components"
              values={{
                organization: merchant.organization.name,
              }}
            />
          </p>
          {merchant.rgpdTermsUrl && (
            <p className={termsClassName}>
              <Trans
                i18nKey="modals.auth.login.rgpdTermsLink"
                ns="components"
                components={{
                  linkRGPD: (
                    <a
                      title="rgpd"
                      className="underline"
                      href={merchant.rgpdTermsUrl}
                      target="_blank"
                      rel="noreferrer noopener"
                    />
                  ),
                }}
              />
            </p>
          )}
        </div>
      </div>
    </Transition>
  );
};
